/**
 * About Scene
 * Credits, version info, and educational standards
 * Scientific journal / research publication aesthetic
 *
 * Developed by Funway Interactive SRL (c) 2026
 */

import Phaser from 'phaser';

const COLORS = {
  deepOcean: 0x040d1a,
  darkBlue: 0x0a1628,
  midBlue: 0x0f2847,
  accentCyan: 0x00d4ff,
  accentTeal: 0x00ffd4,
  accentGreen: 0x00ff88,
  accentPurple: 0xb366ff,
  accentGold: 0xffd700,
  textPrimary: 0xe8f4f8,
  textSecondary: 0x8ba4b4,
  textMuted: 0x4a6070,
};

interface FloatingOrb {
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  baseY: number;
  speed: number;
  size: number;
  color: number;
  phase: number;
}

interface DNAStrand {
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  offset: number;
  speed: number;
}

export class AboutScene extends Phaser.Scene {
  private floatingOrbs: FloatingOrb[] = [];
  private dnaStrands: DNAStrand[] = [];
  private centralCell?: Phaser.GameObjects.Container;
  private cellPulse = 0;

  constructor() {
    super({ key: 'AboutScene' });
  }

  create(): void {
    this.createBackground();
    this.createDNAStrands();
    this.createFloatingOrbs();
    this.createCentralCell();
    this.createContent();
    this.createBackButton();
  }

  override update(_time: number, delta: number): void {
    // Animate floating orbs
    this.floatingOrbs.forEach((orb) => {
      orb.phase += delta * 0.001 * orb.speed;
      orb.y = orb.baseY + Math.sin(orb.phase) * 15;
      orb.x += Math.cos(orb.phase * 0.5) * 0.2;

      orb.graphics.clear();
      // Outer glow
      orb.graphics.fillStyle(orb.color, 0.1);
      orb.graphics.fillCircle(orb.x, orb.y, orb.size * 3);
      orb.graphics.fillStyle(orb.color, 0.2);
      orb.graphics.fillCircle(orb.x, orb.y, orb.size * 2);
      // Core
      orb.graphics.fillStyle(orb.color, 0.5);
      orb.graphics.fillCircle(orb.x, orb.y, orb.size);
    });

    // Animate DNA strands
    this.dnaStrands.forEach((strand) => {
      strand.offset += delta * 0.02 * strand.speed;
      this.drawDNAStrand(strand);
    });

    // Animate central cell
    this.cellPulse += delta * 0.002;
    if (this.centralCell) {
      const scale = 1 + Math.sin(this.cellPulse) * 0.03;
      this.centralCell.setScale(scale);
    }
  }

  private createBackground(): void {
    const { width, height } = this.cameras.main;
    const bg = this.add.graphics();

    // Deep ocean base
    bg.fillStyle(COLORS.deepOcean, 1);
    bg.fillRect(0, 0, width, height);

    // Vertical gradient waves
    for (let i = 0; i < 40; i++) {
      const ratio = i / 40;
      const y = ratio * height;
      const waveOffset = Math.sin(ratio * Math.PI * 2) * 0.01;
      const alpha = 0.015 + waveOffset + Math.sin(ratio * Math.PI) * 0.02;
      bg.fillStyle(COLORS.midBlue, Math.max(0, alpha));
      bg.fillRect(0, y, width, height / 40 + 1);
    }

    // Central radial glow (where the cell will be)
    const centerX = width / 2;
    const centerY = 180;
    for (let i = 25; i >= 0; i--) {
      const radius = 180 * (i / 25);
      const alpha = 0.015 * (1 - i / 25);
      bg.fillStyle(COLORS.accentCyan, alpha);
      bg.fillCircle(centerX, centerY, radius);
    }

    // Bottom ambient glow
    for (let i = 20; i >= 0; i--) {
      const radius = 400 * (i / 20);
      const alpha = 0.008 * (1 - i / 20);
      bg.fillStyle(COLORS.accentPurple, alpha);
      bg.fillCircle(width / 2, height + 100, radius);
    }

    // Corner accent glows
    const glowSpots = [
      { x: 0, y: 0, color: COLORS.accentTeal, size: 200, alpha: 0.006 },
      { x: width, y: height, color: COLORS.accentCyan, size: 250, alpha: 0.008 },
      { x: width, y: 0, color: COLORS.accentPurple, size: 180, alpha: 0.005 },
      { x: 0, y: height, color: COLORS.accentGreen, size: 200, alpha: 0.006 },
    ];

    glowSpots.forEach((glow) => {
      for (let i = 15; i >= 0; i--) {
        const radius = (i / 15) * glow.size;
        const alpha = glow.alpha * (1 - i / 15);
        bg.fillStyle(glow.color, alpha);
        bg.fillCircle(glow.x, glow.y, radius);
      }
    });

    // Subtle scan lines
    const scanLines = this.add.graphics();
    scanLines.lineStyle(1, COLORS.accentCyan, 0.01);
    for (let y = 0; y < height; y += 3) {
      scanLines.lineBetween(0, y, width, y);
    }
  }

  private createDNAStrands(): void {
    // Left DNA strand
    const leftStrand: DNAStrand = {
      graphics: this.add.graphics(),
      x: 50,
      offset: 0,
      speed: 1,
    };
    this.dnaStrands.push(leftStrand);

    // Right DNA strand
    const rightStrand: DNAStrand = {
      graphics: this.add.graphics(),
      x: this.cameras.main.width - 50,
      offset: Math.PI,
      speed: 0.8,
    };
    this.dnaStrands.push(rightStrand);

    // Draw initial state
    this.dnaStrands.forEach((strand) => this.drawDNAStrand(strand));
  }

  private drawDNAStrand(strand: DNAStrand): void {
    const { height } = this.cameras.main;
    strand.graphics.clear();
    strand.graphics.setAlpha(0.15);

    const amplitude = 25;
    const wavelength = 60;

    for (let y = -20; y < height + 20; y += 3) {
      const phase = (y + strand.offset) / wavelength * Math.PI * 2;
      const offset1 = Math.sin(phase) * amplitude;
      const offset2 = Math.sin(phase + Math.PI) * amplitude;

      // First helix strand
      strand.graphics.fillStyle(COLORS.accentCyan, 0.6);
      strand.graphics.fillCircle(strand.x + offset1, y, 2);

      // Second helix strand
      strand.graphics.fillStyle(COLORS.accentPurple, 0.6);
      strand.graphics.fillCircle(strand.x + offset2, y, 2);

      // Connecting rungs (base pairs)
      if (y % 12 === 0) {
        strand.graphics.lineStyle(1, COLORS.accentTeal, 0.4);
        strand.graphics.lineBetween(strand.x + offset1, y, strand.x + offset2, y);
      }
    }
  }

  private createFloatingOrbs(): void {
    const { width, height } = this.cameras.main;
    const orbCount = 25;

    for (let i = 0; i < orbCount; i++) {
      const orb: FloatingOrb = {
        graphics: this.add.graphics(),
        x: Math.random() * width,
        y: Math.random() * height,
        baseY: Math.random() * height,
        speed: 0.5 + Math.random() * 1,
        size: 1 + Math.random() * 3,
        color: [COLORS.accentCyan, COLORS.accentTeal, COLORS.accentGreen, COLORS.accentPurple][
          Math.floor(Math.random() * 4)
        ],
        phase: Math.random() * Math.PI * 2,
      };
      this.floatingOrbs.push(orb);
    }
  }

  private createCentralCell(): void {
    const { width } = this.cameras.main;
    const centerX = width / 2;
    const centerY = 170;

    this.centralCell = this.add.container(centerX, centerY);

    // Outer membrane glow
    const outerGlow = this.add.graphics();
    for (let i = 8; i >= 0; i--) {
      outerGlow.fillStyle(COLORS.accentCyan, 0.03 * (1 - i / 8));
      outerGlow.fillCircle(0, 0, 80 + i * 8);
    }

    // Cell membrane
    const membrane = this.add.graphics();
    membrane.lineStyle(4, COLORS.accentCyan, 0.6);

    // Draw organic membrane shape
    const points: { x: number; y: number }[] = [];
    const baseRadius = 65;
    for (let i = 0; i < 32; i++) {
      const angle = (i / 32) * Math.PI * 2;
      const wobble = Math.sin(angle * 3) * 5 + Math.cos(angle * 5) * 3;
      const r = baseRadius + wobble;
      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    membrane.beginPath();
    membrane.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];

      const cx = (p0.x + p1.x * 2 + p2.x) / 4;
      const cy = (p0.y + p1.y * 2 + p2.y) / 4;
      membrane.lineTo(cx, cy);
    }
    membrane.closePath();
    membrane.strokePath();

    // Cytoplasm fill
    membrane.fillStyle(COLORS.accentCyan, 0.08);
    membrane.fillPath();

    // Nucleus
    const nucleus = this.add.graphics();
    nucleus.fillStyle(COLORS.accentPurple, 0.25);
    nucleus.fillCircle(0, 5, 28);
    nucleus.lineStyle(2, COLORS.accentPurple, 0.6);
    nucleus.strokeCircle(0, 5, 28);

    // Nucleolus
    nucleus.fillStyle(COLORS.accentPurple, 0.5);
    nucleus.fillCircle(5, 8, 10);

    // Organelles - Mitochondria
    const organelles = this.add.graphics();
    const mitoPositions = [
      { x: -35, y: -25, angle: 0.3 },
      { x: 30, y: -30, angle: -0.5 },
      { x: -40, y: 20, angle: 1.2 },
      { x: 35, y: 25, angle: 0.8 },
    ];

    mitoPositions.forEach((pos) => {
      organelles.save();
      organelles.translateCanvas(pos.x, pos.y);
      organelles.rotateCanvas(pos.angle);
      organelles.fillStyle(0xed8936, 0.5);
      organelles.fillEllipse(0, 0, 18, 8);
      organelles.lineStyle(1, 0xed8936, 0.7);
      organelles.strokeEllipse(0, 0, 18, 8);
      // Cristae lines
      organelles.lineStyle(1, 0xed8936, 0.4);
      organelles.lineBetween(-5, -3, -5, 3);
      organelles.lineBetween(0, -3, 0, 3);
      organelles.lineBetween(5, -3, 5, 3);
      organelles.restore();
    });

    // Ribosomes (small dots)
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 30;
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;
      if (Math.abs(x) > 20 || Math.abs(y - 5) > 22) {
        organelles.fillStyle(0xd53f8c, 0.6);
        organelles.fillCircle(x, y, 2);
      }
    }

    // Highlight
    const highlight = this.add.graphics();
    highlight.fillStyle(0xffffff, 0.1);
    highlight.fillEllipse(-25, -30, 25, 15);

    this.centralCell.add([outerGlow, membrane, nucleus, organelles, highlight]);
  }

  private createContent(): void {
    const { width } = this.cameras.main;
    const contentY = 290;

    // Game Title
    const title = this.add.text(width / 2, contentY, 'CELLS & LIFE', {
      fontFamily: '"Courier New", monospace',
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);
    title.setStroke('#001a2e', 3);
    title.setShadow(0, 0, '#00d4ff', 12, true, true);

    // Subtitle
    const subtitle = this.add.text(width / 2, contentY + 40, 'The Living Code', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      fontStyle: 'italic',
      color: '#00ffd4',
    }).setOrigin(0.5);
    subtitle.setShadow(0, 0, '#00ffd4', 6, true, true);

    // Version
    this.add.text(width / 2, contentY + 70, 'Version 1.0.0', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      color: '#8ba4b4',
    }).setOrigin(0.5);

    // Divider
    const divider1 = this.add.graphics();
    divider1.lineStyle(1, COLORS.accentCyan, 0.3);
    divider1.lineBetween(width / 2 - 150, contentY + 95, width / 2 + 150, contentY + 95);
    divider1.fillStyle(COLORS.accentCyan, 0.5);
    divider1.fillCircle(width / 2, contentY + 95, 3);

    // Developer Credit - Main
    const devLabel = this.add.text(width / 2, contentY + 125, 'DEVELOPED BY', {
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      color: '#4a6070',
      letterSpacing: 4,
    }).setOrigin(0.5);

    const devName = this.add.text(width / 2, contentY + 150, 'Funway Interactive SRL', {
      fontFamily: 'Georgia, serif',
      fontSize: '22px',
      color: '#e8f4f8',
    }).setOrigin(0.5);

    const copyright = this.add.text(width / 2, contentY + 178, '© 2026 All Rights Reserved', {
      fontFamily: 'Georgia, serif',
      fontSize: '13px',
      fontStyle: 'italic',
      color: '#8ba4b4',
    }).setOrigin(0.5);

    // Divider
    const divider2 = this.add.graphics();
    divider2.lineStyle(1, COLORS.accentPurple, 0.3);
    divider2.lineBetween(width / 2 - 120, contentY + 210, width / 2 + 120, contentY + 210);

    // Educational Standards Section
    this.add.text(width / 2, contentY + 235, 'ALIGNED WITH', {
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      color: '#4a6070',
      letterSpacing: 4,
    }).setOrigin(0.5);

    // NGSS Badge
    const ngssContainer = this.add.container(width / 2, contentY + 275);

    const ngssBg = this.add.graphics();
    ngssBg.fillStyle(COLORS.darkBlue, 0.9);
    ngssBg.fillRoundedRect(-140, -25, 280, 50, 8);
    ngssBg.lineStyle(1, COLORS.accentGreen, 0.4);
    ngssBg.strokeRoundedRect(-140, -25, 280, 50, 8);

    const ngssIcon = this.add.graphics();
    ngssIcon.fillStyle(COLORS.accentGreen, 0.8);
    ngssIcon.fillCircle(-105, 0, 15);
    ngssIcon.fillStyle(COLORS.darkBlue, 1);
    ngssIcon.fillCircle(-105, 0, 10);
    ngssIcon.fillStyle(COLORS.accentGreen, 0.8);
    ngssIcon.fillCircle(-105, 0, 5);

    const ngssText = this.add.text(-80, -8, 'NGSS', {
      fontFamily: '"Courier New", monospace',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#00ff88',
    });

    const ngssDesc = this.add.text(-80, 10, 'Next Generation Science Standards', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#8ba4b4',
    });

    ngssContainer.add([ngssBg, ngssIcon, ngssText, ngssDesc]);

    // Standards covered
    const standardsText = this.add.text(width / 2, contentY + 320, 'MS-LS1-1 · MS-LS1-2', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      color: '#00d4ff',
    }).setOrigin(0.5);

    // Footer divider
    const divider3 = this.add.graphics();
    divider3.lineStyle(1, COLORS.accentCyan, 0.2);
    divider3.lineBetween(width / 2 - 180, contentY + 355, width / 2 + 180, contentY + 355);

    // Technology credits
    this.add.text(width / 2, contentY + 375, 'POWERED BY', {
      fontFamily: '"Courier New", monospace',
      fontSize: '10px',
      color: '#4a6070',
      letterSpacing: 3,
    }).setOrigin(0.5);

    const techText = this.add.text(width / 2, contentY + 395, 'Phaser 3 · TypeScript · Vite', {
      fontFamily: '"Courier New", monospace',
      fontSize: '11px',
      color: '#8ba4b4',
    }).setOrigin(0.5);

    // Entrance animations
    const elements = [
      { el: title, delay: 100 },
      { el: subtitle, delay: 200 },
      { el: devLabel, delay: 400 },
      { el: devName, delay: 500 },
      { el: copyright, delay: 600 },
      { el: ngssContainer, delay: 800 },
      { el: standardsText, delay: 900 },
      { el: techText, delay: 1000 },
    ];

    elements.forEach(({ el, delay }) => {
      el.setAlpha(0);
      el.y += 20;
      this.tweens.add({
        targets: el,
        alpha: 1,
        y: el.y - 20,
        duration: 500,
        delay,
        ease: 'Power2.out',
      });
    });
  }

  private createBackButton(): void {
    const container = this.add.container(30, 30);

    const glow = this.add.graphics();
    glow.fillStyle(COLORS.accentCyan, 0.08);
    glow.fillRoundedRect(-3, -3, 96, 42, 10);

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.95);
    bg.fillRoundedRect(0, 0, 90, 36, 8);
    bg.lineStyle(1, COLORS.accentCyan, 0.5);
    bg.strokeRoundedRect(0, 0, 90, 36, 8);

    const text = this.add.text(45, 18, '← BACK', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);

    container.add([glow, bg, text]);
    container.setSize(90, 36);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(1000);

    container.on('pointerover', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.15);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 1);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(2, COLORS.accentCyan, 0.8);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.08);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 0.95);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(1, COLORS.accentCyan, 0.5);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00d4ff');
    });

    container.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }
}
